import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { Company, CompanySortOptions } from '@/api/v4/organization.api';
import { useHandleFilters } from '@/components/filters/use-handle-filters';
import { FixedBottomContainer } from '@/components/fixed-bottom-container/fixed-bottom-container';
import { TOPBAR_HEIGHT } from '@/features/topbar/topbar';
import { useCreateActions } from '@/hooks/use-create-actions';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { InfiniteScrollTable } from '@/ui/table/infinite-table/infinite-scroll-table';
import { NoResults } from '@/ui/table/no-results';
import type { ISort } from '@/ui/table/table.types';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { CompaniesFilters } from './filters/companies-filters';
import { useCompaniesFiltersSearchLocationSearch } from './filters/use-companies-filters-location-search';
import { useCompanies } from './hooks/use-companies.query';
import { useCompaniesTableConfiguration } from './hooks/use-companies-table-configuration';
import type { CompanyFilters } from './companies.state';
import {
  appliedCompaniesFiltersState,
  companiesSortState,
  selectedCompaniesFiltersState,
  selectedCompaniesState,
} from './companies.state';
import { CompaniesKPI } from './kpi/companies-kpi';
import { useAtomValue, useSetAtom } from 'jotai';
import { NotificationsSidebar } from '../notifications/notifications-sidebar';

const CONTAINER_PADDING = '30px';

export const CompaniesPageContent = () => {
  const { t } = useTranslation('companies');
  const kpisContainerRef = useRef<HTMLDivElement>(null);

  const appliedFilters = useAtomValue(appliedCompaniesFiltersState);

  const { onAddOrganization } = useCreateActions();
  const { data, isLoading, hasNextPage, fetchNextPage } = useCompanies();

  const companies = data ? data.pages.flat() : [];

  const { header, rows, skeletonConfig } = useCompaniesTableConfiguration({
    companies,
  });

  const { hasRequiredPermission } = useUserPermissions();
  const setSelectedCompanies = useSetAtom(selectedCompaniesState);
  const resetSelectedCompanies = () => setSelectedCompanies([]);

  const handleSort = (sortConfig: ISort<CompanySortOptions | undefined>) => {
    updateLocationSearch(appliedFilters, sortConfig);
    resetSelectedCompanies();
  };

  const { updateLocationSearch, resetLocationSearch } =
    useCompaniesFiltersSearchLocationSearch();

  const { clearAppliedFilters, hasAnyActiveFilters } =
    useHandleFilters<CompanyFilters>({
      appliedFiltersAtom: appliedCompaniesFiltersState,
      selectedFiltersAtom: selectedCompaniesFiltersState,
      updateLocationSearch,
      resetLocationSearch,
    });

  return (
    <CompaniesContentContainer
      name="companies-page-container"
      direction="column"
      padding={`0 ${CONTAINER_PADDING}`}
      fullHeight
    >
      <CompaniesFilters />

      <TableContainer>
        {!isLoading && rows.length === 0 ? (
          <Flexbox
            name="companies-no-results-wrapper"
            justify="center"
            alignItems="center"
            fullHeight
          >
            {hasAnyActiveFilters ? (
              <NoResults
                header={t`companies.filters.noResults.header`}
                text={t`companies.filters.noResults.text`}
                buttonText={t`companies.filters.noResults.button`}
                buttonIcon="ArrowGoBack"
                onButtonClick={clearAppliedFilters}
              />
            ) : (
              <NoResults
                onButtonClick={onAddOrganization}
                buttonText={t`companies.noTableResults.button`}
                header={t`companies.noTableResults.header`}
                text={
                  hasRequiredPermission(PERMISSION.ADD_ORGANIZATION)
                    ? t`companies.noTableResults.text`
                    : ''
                }
                buttonIcon="Plus"
                showButton={hasRequiredPermission(PERMISSION.ADD_ORGANIZATION)}
              />
            )}
          </Flexbox>
        ) : (
          <ScrollableTableContainer id="companies-scroll-container">
            <InfiniteScrollTable<Company, CompanySortOptions>
              id="companies"
              isLoading={isLoading}
              headers={header}
              rows={rows}
              data={companies}
              hasMore={Boolean(hasNextPage)}
              onLoadMore={fetchNextPage}
              onSort={handleSort}
              sortState={companiesSortState}
              height="100%"
              scrollableTarget="companies-scroll-container"
              skeletonConfig={skeletonConfig}
              bodyShadow
            />
          </ScrollableTableContainer>
        )}
      </TableContainer>

      <FixedBottomContainer ref={kpisContainerRef}>
        <CompaniesKPI />
      </FixedBottomContainer>
      <NotificationsSidebar />
    </CompaniesContentContainer>
  );
};

const CompaniesContentContainer = styled(Flexbox)`
  position: relative;
  padding-bottom: 60px;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  height: calc(100vh - ${TOPBAR_HEIGHT});
`;

const TableContainer = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ScrollableTableContainer = styled('div')`
  padding: 0 ${CONTAINER_PADDING} 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
  ${({ theme }) => theme.mixins.scrollbar}
`;
